import React, { useEffect, useState } from 'react';
import API from './services/api'; 

const About = () => {
    const [gallery, setGallery] = useState([]);

    // 🌟 AESTHETIC SCROLL REVEAL LOGIC
    useEffect(() => {
        const observerOptions = {
            threshold: 0.15,
            rootMargin: "0px 0px -50px 0px"
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-revealed');
                    observer.unobserve(entry.target); 
                }
            });
        }, observerOptions);

        const revealElements = document.querySelectorAll('.reveal-elem');
        revealElements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, [gallery]); // Re-run when gallery data loads

    useEffect(() => {
        // Fetch images from the admin-managed backend
        API.get('/get_gallery.php')
            .then(res => {
                if(Array.isArray(res.data)) {
                    setGallery(res.data);
                }
            })
            .catch(err => console.error("Gallery fetch error:", err));
    }, []);

    return (
        <div className="about-wrapper">
            <style>{`
                /* --- BASE & TYPOGRAPHY --- */
                .about-wrapper {
                    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                    background: #FAFBFC; /* Slightly cooler, cleaner off-white */
                    color: #2b2d42;
                    padding-top: 80px; 
                    overflow-x: hidden;
                    line-height: 1.6;
                }

                .container-custom {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 24px;
                }

                /* --- AESTHETIC REVEAL ANIMATIONS --- */
                .reveal-elem {
                    opacity: 0;
                    transition: all 1.2s cubic-bezier(0.16, 1, 0.3, 1); /* Smoother, premium easing */
                }
                .reveal-elem.is-revealed { opacity: 1; }

                .anim-up { transform: translateY(40px); }
                .anim-up.is-revealed { transform: translateY(0); }

                .anim-left { transform: translateX(-50px); }
                .anim-left.is-revealed { transform: translateX(0); }

                .anim-right { transform: translateX(50px); }
                .anim-right.is-revealed { transform: translateX(0); }

                .anim-scale { transform: scale(0.95); opacity: 0; }
                .anim-scale.is-revealed { transform: scale(1); opacity: 1; }

                /* --- HERO HEADER (PREMIUM) --- */
                .about-hero {
                    position: relative;
                    background: linear-gradient(135deg, rgba(10, 35, 20, 0.92) 0%, rgba(19, 70, 40, 0.85) 100%), 
                                url('/campus pic.jpg') center/cover no-repeat;
                    padding: 140px 20px;
                    text-align: center;
                    border-bottom: 6px solid #ffc300;
                    margin-bottom: 100px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }

                .about-hero h1 {
                    font-size: clamp(2.5rem, 8vw, 5rem);
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 6px;
                    background: linear-gradient(to right, #ffffff, #ffc300, #ffffff);
                    background-size: 200% auto;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    animation: textShine 4s linear infinite, aestheticFadeUp 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    margin-bottom: 15px;
                }

                .about-hero p {
                    color: rgba(255, 255, 255, 0.9);
                    font-size: clamp(1.1rem, 2.5vw, 1.4rem);
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    opacity: 0;
                    animation: aestheticFadeUp 1.5s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards;
                }

                /* --- HERITAGE SECTION --- */
                .heritage-flex-row {
                    display: flex;
                    align-items: center;
                    gap: 80px;
                    margin-bottom: 120px;
                }

                .heritage-text-content { 
                    flex: 1; 
                }
                
                .heritage-text-content h2 {
                    color: #134628;
                    font-weight: 900;
                    font-size: clamp(2rem, 4vw, 3rem);
                    margin-bottom: 30px;
                    position: relative;
                    padding-bottom: 15px;
                }

                /* Elegant animated underline */
                .heritage-text-content h2::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    width: 60px;
                    height: 4px;
                    background: #ffc300;
                    border-radius: 2px;
                    transition: width 0.4s ease;
                }
                .heritage-text-content:hover h2::after { width: 120px; }

                .heritage-paragraph {
                    font-size: 1.15rem;
                    color: #4a5568;
                    line-height: 1.9;
                    text-align: justify;
                    margin-bottom: 30px;
                }

                .premium-quote {
                    background: #ffffff;
                    border-left: 5px solid #134628;
                    padding: 30px 40px;
                    border-radius: 0 20px 20px 0;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.03);
                    position: relative;
                }

                .premium-quote::before {
                    content: '"';
                    position: absolute;
                    top: -10px;
                    left: 15px;
                    font-size: 4rem;
                    color: rgba(19, 70, 40, 0.1);
                    font-family: serif;
                    line-height: 1;
                }

                .premium-quote p {
                    font-style: italic;
                    font-weight: 700;
                    color: #134628;
                    margin: 0;
                    font-size: 1.2rem;
                    line-height: 1.6;
                }

                .heritage-image-container {
                    flex: 1;
                    position: relative;
                }

                /* Offset Accent Frame behind the image */
                .image-accent-frame {
                    position: absolute;
                    top: -20px;
                    right: -20px;
                    bottom: 20px;
                    left: 20px;
                    border: 3px solid #ffc300;
                    border-radius: 24px;
                    z-index: 0;
                    transition: transform 0.5s ease;
                }

                .heritage-image-wrapper {
                    position: relative;
                    z-index: 1;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 25px 50px rgba(19, 70, 40, 0.15);
                    transition: transform 0.5s ease;
                }

                .heritage-image-container:hover .heritage-image-wrapper { transform: translate(-5px, -5px); }
                .heritage-image-container:hover .image-accent-frame { transform: translate(5px, 5px); }

                /* --- GALLERY SECTION --- */
                .gallery-section {
                    padding: 100px 0;
                    background: #ffffff;
                }

                .section-header {
                    text-align: center;
                    margin-bottom: 70px;
                }

                .section-header h2 {
                    color: #134628;
                    font-weight: 900;
                    font-size: clamp(2.5rem, 5vw, 3.5rem);
                    text-transform: uppercase;
                    margin-bottom: 10px;
                }

                .gold-bar-center {
                    width: 60px;
                    height: 4px;
                    background: #ffc300;
                    margin: 0 auto 20px;
                    border-radius: 2px;
                }

                .gallery-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    grid-auto-rows: 300px;
                    gap: 24px;
                }

                .gallery-item {
                    position: relative;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 10px 20px rgba(0,0,0,0.06);
                    cursor: pointer;
                    /* Fix for Safari border-radius clipping */
                    -webkit-mask-image: -webkit-radial-gradient(white, black); 
                }

                .gallery-item:nth-child(4n+1) { grid-column: span 2; grid-row: span 2; }

                .gallery-item img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transition: transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .gallery-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(to top, rgba(10, 35, 20, 0.95) 0%, rgba(19, 70, 40, 0.2) 50%, transparent 100%);
                    opacity: 0;
                    display: flex;
                    align-items: flex-end;
                    padding: 30px;
                    transition: opacity 0.4s ease;
                }

                .gallery-item:hover img { transform: scale(1.08); }
                .gallery-item:hover .gallery-overlay { opacity: 1; }

                .gallery-overlay p { 
                    margin: 0; 
                    font-weight: 700; 
                    color: #ffc300; 
                    font-size: 1.1rem;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    transform: translateY(20px);
                    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .gallery-item:hover .gallery-overlay p { transform: translateY(0); }

                /* --- PILLARS SECTION (GLASSMORPHISM) --- */
                .pillars-bg {
                    position: relative;
                    background: #0A1C12;
                    /* Advanced radial spotlight effect */
                    background-image: radial-gradient(circle at 50% 0%, #1a5933 0%, #0A1C12 70%);
                    padding: 140px 0;
                    color: #fff;
                    overflow: hidden;
                }

                /* Decorative background element */
                .pillars-bg::before {
                    content: '';
                    position: absolute;
                    top: -50%; left: -10%; width: 50vw; height: 50vw;
                    background: rgba(255, 195, 0, 0.03);
                    border-radius: 50%;
                    filter: blur(80px);
                    pointer-events: none;
                }

                .pillars-title {
                    text-align: center;
                    color: #ffffff;
                    font-size: clamp(2.5rem, 5vw, 3.5rem);
                    font-weight: 900;
                    margin-bottom: 80px;
                    text-transform: uppercase;
                    position: relative;
                    z-index: 2;
                }

                .pillars-title span { color: #ffc300; }

                .pillars-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
                    gap: 40px;
                    position: relative;
                    z-index: 2;
                }

                .pillar-card {
                    background: rgba(255, 255, 255, 0.03);
                    backdrop-filter: blur(16px);
                    -webkit-backdrop-filter: blur(16px);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-top: 1px solid rgba(255, 195, 0, 0.3);
                    padding: 60px 40px;
                    border-radius: 24px;
                    text-align: center;
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    position: relative;
                    overflow: hidden;
                }

                /* Subtle hover glow inside card */
                .pillar-card::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; width: 100%; height: 100%;
                    background: linear-gradient(180deg, rgba(255,195,0,0.05) 0%, transparent 100%);
                    opacity: 0;
                    transition: opacity 0.5s ease;
                }

                .pillar-card:hover {
                    transform: translateY(-10px);
                    border-top-color: #ffc300;
                    background: rgba(255, 255, 255, 0.06);
                    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 195, 0, 0.05);
                }
                .pillar-card:hover::before { opacity: 1; }

                .pillar-icon-wrapper {
                    width: 90px;
                    height: 90px;
                    background: linear-gradient(135deg, rgba(255, 195, 0, 0.15) 0%, rgba(255, 195, 0, 0.05) 100%);
                    border: 1px solid rgba(255, 195, 0, 0.2);
                    border-radius: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 30px;
                    font-size: 2.5rem;
                    color: #ffc300;
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                    transform: rotate(-5deg);
                }

                .pillar-card:hover .pillar-icon-wrapper {
                    transform: rotate(0deg) scale(1.1);
                    background: #ffc300;
                    color: #134628;
                    box-shadow: 0 15px 30px rgba(255, 195, 0, 0.3);
                }

                .pillar-card h4 {
                    font-weight: 800;
                    font-size: 1.4rem;
                    text-transform: uppercase;
                    margin-bottom: 20px;
                    color: #ffffff;
                    letter-spacing: 1px;
                }

                .pillar-card p {
                    color: rgba(255, 255, 255, 0.7);
                    font-size: 1.05rem;
                    line-height: 1.8;
                    margin: 0;
                }

                /* --- KEYFRAMES --- */
                @keyframes aestheticFadeUp { 
                    from { opacity: 0; transform: translateY(30px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }
                
                @keyframes textShine {
                    0% { background-position: 0% center; }
                    100% { background-position: -200% center; }
                }

                /* --- MOBILE RESPONSIVENESS (OPTIMIZED) --- */
                @media (max-width: 992px) {
                    .heritage-flex-row { flex-direction: column; gap: 60px; }
                    .heritage-image-container { width: 100%; max-width: 600px; margin: 0 auto; }
                    
                    /* Reset complex grid spans on smaller screens */
                    .gallery-item:nth-child(4n+1) { grid-column: span 1; grid-row: span 1; }
                    .gallery-grid { grid-auto-rows: 250px; }
                    
                    .pillars-bg { padding: 100px 0; }
                }

                @media (max-width: 768px) {
                    /* Outer container adjustments to reclaim horizontal space */
                    .container-custom { padding: 0 15px; }

                    .about-hero { padding: 120px 20px 80px; margin-bottom: 60px; }
                    .about-hero h1 { font-size: 2.2rem; letter-spacing: 3px; }
                    
                    .heritage-flex-row { margin-bottom: 60px; gap: 40px; }
                    .heritage-text-content h2 { font-size: 2rem; }
                    .premium-quote { padding: 20px 25px; border-radius: 0 16px 16px 0; }
                    .premium-quote p { font-size: 1.05rem; }

                    /* --- PILLARS MOBILE FIXES --- */
                    .pillars-bg { 
                        /* 120px top padding forces the title down BELOW your sticky navbar */
                        padding: 120px 0 80px; 
                    }
                    
                    .pillars-title { 
                        font-size: 2rem; 
                        margin-bottom: 40px; 
                        line-height: 1.2;
                    }
                    
                    /* 🌟 THE FIX: Force exactly 1 flexible column on mobile */
                    .pillars-grid { 
                        grid-template-columns: 1fr; 
                        gap: 20px; 
                    }
                    
                    /* Drastically reduce padding so text isn't squished */
                    .pillar-card { 
                        padding: 30px 20px; 
                        border-radius: 16px;
                    }
                    
                    /* Shrink the icon so it doesn't dominate the card */
                    .pillar-icon-wrapper { 
                        width: 60px; 
                        height: 60px; 
                        font-size: 1.8rem; 
                        margin-bottom: 15px; 
                    }
                    
                    .pillar-card h4 { 
                        font-size: 1.2rem; 
                        margin-bottom: 10px; 
                    }
                    
                    /* Brighten text slightly and improve line breaks */
                    .pillar-card p { 
                        font-size: 0.95rem; 
                        line-height: 1.5; 
                        opacity: 0.9; 
                    }
                }

                /* Ultra-narrow devices (like the 360px emulator you are using) */
                @media (max-width: 380px) {
                    .about-hero h1 { font-size: 1.8rem; }
                    .pillars-title { font-size: 1.8rem; }
                    .pillar-card { padding: 25px 15px; } /* Maximum space for text */
                }

            `}</style>

            <header className="about-hero">
                <div className="container-custom">
                    <h1>OUR HERITAGE</h1>
                    <p>Legacy of Karnal Sher Khan Cadet College Swabi</p>
                </div>
            </header>

            <div className="container-custom">
                <section className="heritage-flex-row">
                    <div className="heritage-text-content reveal-elem anim-left">
                        <h2>Honor and Sacrifice</h2>
                        <p className="heritage-paragraph">
                            The Cadet College is dedicated to <strong>Captain Karnal Sher Khan Shaheed (NH)</strong>. 
                            He was born on 1st January 1970 and emerged as a symbol of courage during the Kargil conflict.
                            Setting personal examples of bravery, he defended strategic posts at a height of 17,000 feet 
                            and repulsed many attacks before embracing 'SHAHADAT' on 5th July 1999.
                        </p>
                        <div className="premium-quote">
                            <p>"His faith and courage remain an eternal inspiration for every cadet who walks these halls."</p>
                        </div>
                    </div>
                    <div className="heritage-image-container reveal-elem anim-right" style={{transitionDelay: '0.2s'}}>
                        <div className="image-accent-frame"></div>
                        <div className="heritage-image-wrapper">
                            <img src="/campus pic.jpg" alt="Sherian Campus" style={{width: '100%', display: 'block'}} />
                        </div>
                    </div>
                </section>
            </div>

            {/* --- COLLEGE GALLERY SECTION --- */}
            <section className="gallery-section">
                <div className="container-custom">
                    <div className="section-header reveal-elem anim-up">
                        <h2>Sherian Gallery</h2>
                        <div className="gold-bar-center"></div>
                        <p className="text-muted" style={{fontSize: '1.2rem'}}>Glimpses of Sherian Life and Lasting Legacy</p>
                    </div>

                    <div className="gallery-grid">
                        {gallery.length > 0 ? (
                            gallery.map((img, idx) => (
                                <div className="gallery-item reveal-elem anim-scale" key={idx} style={{ transitionDelay: `${(idx % 4) * 0.1}s` }}>
                                    <img src={`https://sherianalumni.com/api/uploads/gallery/${img.image_path}`} alt="Gallery" loading="lazy" />
                                    <div className="gallery-overlay">
                                        <p>{img.caption || 'Sherian Campus Moment'}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            /* FALLBACK PLACEHOLDERS */
                            [1, 2, 3, 4, 5, 6].map((item, idx) => (
                                <div className="gallery-item reveal-elem anim-scale" key={item} style={{ transitionDelay: `${(idx % 4) * 0.1}s` }}>
                                    <img src={`https://picsum.photos/800/600?random=${item}`} alt="Campus" loading="lazy" />
                                    <div className="gallery-overlay">
                                        <p>Sherian Campus Life</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>

            {/* --- PILLARS SECTION --- */}
            <section className="pillars-bg">
                <div className="container-custom">
                    <h2 className="pillars-title reveal-elem anim-up"><span>Core</span> Pillars</h2>
                    <div className="pillars-grid">
                        <div className="pillar-card reveal-elem anim-up" style={{ transitionDelay: '0.1s' }}>
                            <div className="pillar-icon-wrapper"><i className="fas fa-graduation-cap"></i></div>
                            <h4>Empowering Education</h4>
                            <p>Building a stronger future for Karnal Sher Khan Cadet College through scholarships, academic support, library development, infrastructure, and educational initiatives.</p>
                        </div>
                        <div className="pillar-card reveal-elem anim-up" style={{ transitionDelay: '0.2s' }}>
                            <div className="pillar-icon-wrapper"><i className="fas fa-handshake"></i></div>
                            <h4>Connecting Sherians</h4>
                            <p>Fostering lifelong brotherhood by strengthening alumni networks, regional chapters, reunions, mentorship, and meaningful engagement among Sherians worldwide.</p>
                        </div>
                        <div className="pillar-card reveal-elem anim-up" style={{ transitionDelay: '0.3s' }}>
                            <div className="pillar-icon-wrapper"><i className="fas fa-globe"></i></div>
                            <h4>Creating Lasting Impact</h4>
                            <p>Driving positive change through career development, community service, fundraising, leadership, and initiatives that benefit alumni, cadets, and society.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;